// The billing app's copy of the water restriction level list, and the date
// questions the Manage Municipalities pills ask. The authority is
// workers/shared/billing-core/restrictions.ts; the app does not import from
// workers/, so keep the list and labels identical (pinned by the test).
//
// Entries cover [effective_from, effective_to): the end is the EXCLUSIVE 1st
// of the month the restriction stops, the bill-run convention.

import type { RestrictionLevel, WaterRestriction } from '../api/types';

export const RESTRICTION_LEVELS: readonly RestrictionLevel[] = ['level_1', 'level_2', 'level_3', 'emergency'];
export const LEVEL_LABELS: Record<RestrictionLevel, string> = {
  level_1: 'Level 1', level_2: 'Level 2', level_3: 'Level 3', emergency: 'Emergency',
};
export const WATER_WISE_LABEL = 'Water Wise (no restriction)';

/** Only per-kL water and sanitation vary with the level. */
export const isRestrictableChargeType = (t: string): boolean => t === 'water_kl' || t === 'sanitation_kl';

/** One step milder: Emergency to Level 3 ... Level 1 to Water Wise (null). */
export function stepDown(level: RestrictionLevel): RestrictionLevel | null {
  const i = RESTRICTION_LEVELS.indexOf(level);
  return i <= 0 ? null : RESTRICTION_LEVELS[i - 1];
}

/**
 * Today in the browser's calendar (SAST for every operator). Display only:
 * bills take their level from their own period, never from today. Built from
 * local date parts, never toISOString(), which is UTC (lib/billRunPeriod.ts).
 */
export function todayKey(now: Date = new Date()): string {
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
}

type Entry = Pick<WaterRestriction, 'level' | 'effective_from' | 'effective_to'>;

const covers = (e: Entry, day: string) => e.effective_from <= day && (e.effective_to === null || day < e.effective_to);

/** The level in force on `day` (null = Water Wise). If two ever matched, the later start wins, as billing-core decides. */
export function levelOn(entries: readonly Entry[], day: string): RestrictionLevel | null {
  let hit: Entry | null = null;
  for (const e of entries) {
    if (covers(e, day) && (hit === null || e.effective_from > hit.effective_from)) hit = e;
  }
  return hit ? hit.level : null;
}

/** The next day after `today` the level changes, and to what; null when nothing is scheduled. */
export function nextChange(entries: readonly Entry[], today: string): { level: RestrictionLevel | null; from: string } | null {
  const current = levelOn(entries, today);
  const days = [...new Set(entries.flatMap(e => [e.effective_from, e.effective_to]))]
    .filter((d): d is string => d !== null && d > today)
    .sort();
  let level = current;
  for (const d of days) {
    const next = levelOn(entries, d);
    if (next !== level) return { level: next, from: d };
    level = next;
  }
  return null;
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** '2026-11-01' -> '1 Nov 2026'. */
export function dayLabel(key: string): string {
  const [y, m, d] = key.slice(0, 10).split('-').map(Number);
  return `${d} ${MONTHS[m - 1]} ${y}`;
}

// The day after a date key (mirrors billing-core nextDayKey; the app does not
// import billing-core).
function nextDayKey(key: string): string {
  const d = new Date(`${key.slice(0, 10)}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + 1);
  return d.toISOString().slice(0, 10);
}

function rangeLabel(from: string, to: string): string {
  const [fy, fm, fd] = from.split('-').map(Number);
  const [ty, tm] = to.split('-').map(Number);
  if (fy !== ty) return `${dayLabel(from)} to ${dayLabel(to)}`;
  if (fm !== tm) return `${fd} ${MONTHS[fm - 1]} to ${dayLabel(to)}`;
  return `${fd} to ${dayLabel(to)}`;
}

/** Every day from `from` to `to`, both inclusive. */
export function daySpan(from: string, to: string): string[] {
  const days: string[] = [];
  for (let d = from.slice(0, 10); d <= to.slice(0, 10); d = nextDayKey(d)) days.push(d);
  return days;
}

/** Distinct days as runs, in date order: ['2026-12-02', '2026-12-01', '2026-12-05'] gives '1 to 2 Dec 2026, 5 Dec 2026'. */
export function dayRangesLabel(keys: readonly string[]): string {
  const days = [...new Set(keys.map(k => k.slice(0, 10)))].sort();
  const runs: Array<[string, string]> = [];
  for (const d of days) {
    const last = runs[runs.length - 1];
    if (last && nextDayKey(last[1]) === d) last[1] = d;
    else runs.push([d, d]);
  }
  return runs.map(([from, to]) => (from === to ? dayLabel(from) : rangeLabel(from, to))).join(', ');
}

/** An <input type="month"> value and the stored 1st. */
export const monthToKey = (month: string): string => `${month}-01`;
export const keyToMonth = (key: string): string => key.slice(0, 7);
